<script lang="ts">
  // Minimal non-blocking, NON-MODAL, auto-dismissing NEUTRAL status toast near the
  // top of the canvas (DESIGN components.status-toast neutral variant:
  // surface-container-based, NOT the error-container variant). Story 5.1 uses it for
  // the single invalid-shared-link startup notice; Story 5.2 will generalise the
  // same surface for share-success. Renders nothing when `message` is empty. It never
  // blocks interaction and never requires acknowledgement (a hard DESIGN constraint):
  // the full simulator is usable behind it. Reduced-motion safe (no essential state
  // is conveyed by motion; global.css also neutralises transition durations).
  let { message = '' }: { message?: string } = $props();

  const AUTO_DISMISS_MS = 8000;
  let dismissed = $state(false);

  // Show whenever there is a message and it has not been dismissed. A fresh message
  // re-arms the auto-dismiss timer.
  const visible = $derived(message !== '' && !dismissed);

  $effect(() => {
    if (message === '') return;
    dismissed = false;
    const timer = setTimeout(() => {
      dismissed = true;
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  });

  function dismiss(): void {
    dismissed = true;
  }
</script>

{#if visible}
  <div class="status-toast" data-testid="status-toast" role="presentation">
    <span class="material-symbols-outlined toast-icon" aria-hidden="true">info</span>
    <span class="toast-message">{message}</span>
    <button
      type="button"
      class="toast-dismiss"
      data-testid="status-toast-dismiss"
      aria-label="Dismiss notice"
      onclick={dismiss}
    >
      <span class="material-symbols-outlined" aria-hidden="true">close</span>
    </button>
  </div>
{/if}

<style>
  /* Neutral variant: surface-container-based pill near the top of the canvas.
     Non-modal, never blocks the canvas below (pointer-events on the toast only). */
  .status-toast {
    position: absolute;
    top: calc(var(--top-bar-height) + 12px);
    left: 50%;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: min(560px, calc(100vw - 48px));
    padding: 10px 12px 10px 16px;
    border-radius: var(--radius-full);
    border: 1px solid var(--outline-variant);
    background: color-mix(in srgb, var(--surface-container) 92%, transparent);
    color: var(--on-surface);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    z-index: 30;
  }

  .toast-icon {
    font-size: 20px;
    color: var(--on-surface-variant);
    flex: 0 0 auto;
  }

  .toast-message {
    font-size: 13px;
    line-height: 1.35;
  }

  .toast-dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--on-surface-variant);
    cursor: pointer;
    flex: 0 0 auto;
  }

  .toast-dismiss:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--on-surface);
  }

  .toast-dismiss .material-symbols-outlined {
    font-size: 18px;
  }
</style>
